import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Paginate } from '@shared/entities/paginate';
import { User } from '@shared/entities/user';
import { Roles } from '@shared/models/types';
import { ApiService, Pagination, PER_PAGE } from '@shared/services';
import { BehaviorSubject, Observable, Subject, catchError, of, shareReplay, switchMap, take, tap } from 'rxjs';

const LOG_PREFIX = '[Api][User] ';

export interface UserFilterURL {
  keyword?: string;
  role?: string;
  roles?: Roles[];
  status?: string;
  serialNumber?: string;
  accountId?: string;
  direction?: 'ASC' | 'DESC';
  id?: string;
  orderBy?: string;
}

@Injectable({
  providedIn: 'root'
})
export class UserMisalignedService extends ApiService<User> {
  private _refresh$: Subject<Pagination>;
  allUsers$: Observable<Paginate<User>>;
  private _currentPaginator: Pagination;
  private _currentFilters: UserFilterURL = {};

  set filters(value: UserFilterURL) {
    this._currentFilters = value;
    this.refresh();
  }

  constructor(private _activatedRoute: ActivatedRoute) {
    const masterUrl = '/users/misaligned';
    const detailUrl = '/users/misaligned';

    super(User, masterUrl, detailUrl);

    this._currentPaginator = { page: 1, perPage: PER_PAGE };

    this._refresh$ = new BehaviorSubject<Pagination>(this._currentPaginator);
    this.allUsers$ = this._refresh$.pipe(
      switchMap((pagination) => {
        const { locationId } = this._activatedRoute.snapshot.queryParams;
        if (locationId) {
          this.setUrlParameter('locationId', locationId);
        }
        if (this._currentFilters.keyword !== undefined) {
          this.setUrlParameter('keyword', this._currentFilters.keyword);
        }
        if (this._currentFilters.role !== undefined) {
          this.setUrlParameter('role', this._currentFilters.role);
        }
        if (this._currentFilters.status !== undefined) {
          this.setUrlParameter('status', this._currentFilters.status);
        }
        if (this._currentFilters.serialNumber !== undefined) {
          this.setUrlParameter('serialNumber', this._currentFilters.serialNumber);
        }
        if (this._currentFilters.accountId !== undefined) {
          this.setUrlParameter('accountId', this._currentFilters.accountId);
        }
        if (this._currentFilters.roles !== undefined) {
          if (this._currentFilters.roles && this._currentFilters.roles.length > 0) {
            this.setUrlParameter('roles', this._currentFilters.roles.join(','));
          }
        }
        return this.paginate(User, pagination.page, pagination.perPage);
      }),
      tap({
        next: (result) => this.logger.debug(LOG_PREFIX + 'all$ users', result),
        error: (err) => this.logger.error(LOG_PREFIX + 'all$ users error', err)
      }),
      shareReplay(1)
    );
  }

  refresh() {
    this.logger.debug(LOG_PREFIX + 'refresh users');
    this._refresh$.next(this._currentPaginator);
    return this.getMisalignedUsersCount().pipe(take(1));
  }

  getMisalignedUsersCount(): Observable<number> {
    this.logger.debug(LOG_PREFIX + 'get misaligned users count');

    const { locationId } = this._activatedRoute.snapshot.queryParams;
    if (locationId) {
      this.setUrlParameter('locationId', locationId);
    }
    if (this._currentFilters.keyword !== undefined) {
      this.setUrlParameter('keyword', this._currentFilters.keyword);
    }
    if (this._currentFilters.role !== undefined) {
      this.setUrlParameter('role', this._currentFilters.role);
    }
    if (this._currentFilters.status !== undefined) {
      this.setUrlParameter('status', this._currentFilters.status);
    }
    if (this._currentFilters.serialNumber !== undefined) {
      this.setUrlParameter('serialNumber', this._currentFilters.serialNumber);
    }
    if (this._currentFilters.accountId !== undefined) {
      this.setUrlParameter('accountId', this._currentFilters.accountId);
    }
    if (this._currentFilters.roles !== undefined) {
      if (this._currentFilters.roles && this._currentFilters.roles.length > 0) {
        this.setUrlParameter('roles', this._currentFilters.roles.join(','));
      }
    }

    return this.getCount().pipe(
      catchError((error) => {
        this.logger.error(LOG_PREFIX + 'get misaligned users count error', error);
        return of(0);
      })
    );
  }
}
